import React, { useState, useEffect } from "react"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faPlay,
  faFileExport,
  faIndent,
  faLayerGroup,
  faDiagramProject,
  faFingerprint,
} from "@fortawesome/free-solid-svg-icons"
import ExportModal from "shared/ui/ExportModal/ExportModal.component"
import MenuButton, { MenuButtonItem } from "shared/ui/MenuButton/MenuButton.component"
import ExportDataModal from "./ExportDataModal.component"
import { Tooltip } from "antd"
import { QueryInstance } from "types/engine.types"
import { PROJECT_TAB_KEYS } from "constants/projectTabs"

interface QueryConsoleActionsProps {
  /** Function to run the script written in the editor */
  runScript: () => void
  /** Function to save the project query */
  saveProjectQuery: (queryName: string) => void
  /** The query instance ID */
  queryInstance: QueryInstance | null
  /** Boolean indicating whether the script is currently running */
  isRunningScript: boolean
  /** Whether the current view has a runnable script (e.g. Builder needs row+col vars) */
  canRun: boolean
  /** The selected query */
  selectedQuery: string
  /** Function to set the selected query */
  setSelectedQuery: (query: string) => void
  /** Boolean indicating whether the content has changed */
  contentChanged: boolean
  /** The active key of the tab */
  activeKey?: string
  /** Hide the Run button when the current view doesn't run from this row
   *  (e.g. Transform has its own Apply button). */
  hideRun?: boolean
  /** Hide the Save button when the current view has its own kind-aware
   *  Save (Builder toolbar / Transform pipeline bar). */
  hideSave?: boolean
  /** When true, the Save button always opens the "Save as" modal — used
   *  when the editor is showing a structured record (pipeline/builder)
   *  so we don't accidentally overwrite it with a script-kind body. */
  forceSaveAs?: boolean
  /** Whether the project has a Run on load list: a dot on Tools, a tick on the item. */
  runOnLoadOn: boolean
  /** Opens the Run on load dialog. */
  onOpenRunOnLoad: () => void
  /** Opens Variable lineage: what each variable derives from and what it affects. */
  onOpenLineage: () => void
  /** Opens the version ID and data check on the Provenance tab. */
  onOpenVersionCheck: () => void
  /** Re-indents the script; given in Console mode only, where the editor shows. */
  onFormatScript?: () => void
}

const isMac = (): boolean =>
  window.navigator.platform.toLowerCase().includes("mac")

/**
 * QueryConsoleActions: the right end of the action row, "Tools ▾", "Save ▾"
 * and Run, the one solid button.
 *
 * Tools is a plain item list so other features add an entry rather than a
 * button. Save keeps Ctrl + S and its saved-state tooltip.
 */
const QueryConsoleActions: React.FC<QueryConsoleActionsProps> = ({
  runScript,
  saveProjectQuery,
  queryInstance,
  isRunningScript,
  canRun,
  selectedQuery,
  setSelectedQuery,
  contentChanged,
  activeKey,
  hideRun = false,
  hideSave = false,
  forceSaveAs = false,
  runOnLoadOn,
  onOpenRunOnLoad,
  onOpenLineage,
  onOpenVersionCheck,
  onFormatScript,
}) => {
  const [isSaveQueryModalVisible, setIsSaveQueryModalVisible] =
    useState<boolean>(false)
  const [isExportOpen, setIsExportOpen] = useState<boolean>(false)
  const isUnsaved: boolean = contentChanged && selectedQuery !== ""
  const saveShortcut: string = isMac() ? "⌘ + S" : "Ctrl + S"

  const confirmSaveQuery = (queryName: string): void => {
    saveProjectQuery(queryName)
    setSelectedQuery(queryName)
    setIsSaveQueryModalVisible(false)
  }

  const handleSaveClick = (): void => {
    if (selectedQuery && !forceSaveAs) {
      confirmSaveQuery(selectedQuery)
    } else {
      setIsSaveQueryModalVisible(true)
    }
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      const isReportingTab =
        String(activeKey) === PROJECT_TAB_KEYS.DATA_REPORTING
      if (!isReportingTab) return

      if ((event.ctrlKey || event.metaKey) && event.key === "s") {
        event.preventDefault()
        handleSaveClick()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQuery])

  const saveTooltipTitle = !selectedQuery
    ? `Save query (${saveShortcut})`
    : isUnsaved
    ? `Unsaved changes. Save (${saveShortcut})`
    : `Saved (${saveShortcut})`

  const toolsItems: MenuButtonItem[] = [
    {
      key: "lineage",
      label: "Variable lineage",
      icon: <FontAwesomeIcon icon={faDiagramProject} />,
      disabled: !queryInstance,
      onClick: onOpenLineage,
    },
    {
      key: "version-check",
      label: "Version check",
      icon: <FontAwesomeIcon icon={faFingerprint} />,
      onClick: onOpenVersionCheck,
    },
    {
      key: "run-on-load",
      label: "Run on load",
      icon: <FontAwesomeIcon icon={faLayerGroup} />,
      checked: runOnLoadOn,
      onClick: onOpenRunOnLoad,
    },
    {
      key: "export",
      label: "Export data",
      icon: <FontAwesomeIcon icon={faFileExport} />,
      disabled: !queryInstance,
      onClick: () => setIsExportOpen(true),
    },
    { type: "divider", key: "editor-divider" },
    {
      key: "format-script",
      label: "Format script",
      icon: <FontAwesomeIcon icon={faIndent} />,
      shortcut: "Shift + Alt + F",
      hidden: !onFormatScript,
      onClick: onFormatScript,
    },
  ]

  const saveItems: MenuButtonItem[] = [
    { key: "save", label: "Save", shortcut: saveShortcut, onClick: handleSaveClick },
    {
      key: "save-as",
      label: "Save as",
      onClick: () => setIsSaveQueryModalVisible(true),
    },
  ]

  return (
    <>
      <ExportModal
        title="Save Query"
        isVisible={isSaveQueryModalVisible}
        confirm={confirmSaveQuery}
        cancel={() => setIsSaveQueryModalVisible(false)}
        message="Please enter a filename for your query"
        ExportButtonText="Save"
      />
      <MenuButton label="Tools" items={toolsItems} dot={runOnLoadOn} />
      {!hideSave && (
        <Tooltip
          title={saveTooltipTitle}
          placement="top"
          overlayClassName="dataq-tooltip"
        >
          <span className="inline-block">
            <MenuButton label="Save" items={saveItems} />
          </span>
        </Tooltip>
      )}
      {!hideRun && (
        <Tooltip
          title={
            !canRun
              ? "Add at least one row and one column variable"
              : isRunningScript
              ? "Running…"
              : ""
          }
          placement="top"
          overlayClassName="dataq-tooltip"
        >
          <span className="inline-block">
            <DataQButton
              isDisabled={isRunningScript || !canRun}
              onClick={() => runScript()}
              type="primary"
            >
              Run
              <FontAwesomeIcon icon={faPlay} />
            </DataQButton>
          </span>
        </Tooltip>
      )}
      <ExportDataModal
        queryInstance={queryInstance}
        open={isExportOpen}
        onOpenChange={setIsExportOpen}
      />
    </>
  )
}

export default QueryConsoleActions
